import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { Box, CircularProgress, MenuItem, Typography } from '@mui/material';
import styled from 'styled-components';
import {
  DropDown,
  Flex,
  FlexColumn,
  OverflowTooltip,
  SearchField,
  SvgIcon,
} from '@elementary/common-ui/components';
import useTicketSearch, {
  extractTicketKey,
  type TicketSelection,
} from 'shared/hooks/useTicketSearch';
import type { TicketingIntegration } from 'shared/types';
import { ticketingIconMap } from 'shared/components/ticketing/utils';
import {
  fontSize,
  fontWeight,
  radius,
  space,
} from '@elementary/common-ui/style/utils';

const PanelRoot = styled(FlexColumn)`
  width: 320px;
  max-height: 400px;
  min-height: 0;
`;

const PanelHeader = styled(FlexColumn)`
  padding: ${space('sm', 'sm', 0)};
  gap: ${space('sm')};
`;

const PanelScrollArea = styled(Box)`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: ${space('sm', 'xs', 'xs')};
`;

const TicketMenuItem = styled(MenuItem)`
  min-height: 0;
  height: 36px;
  padding: ${space('lg')};
  border-radius: ${radius('lg')};

  .MuiTypography-root {
    font-size: ${fontSize('base')};
  }

  :hover {
    background: ${({ theme }) => theme.palette.surface.tinted};
  }
`;

const CreateTicketActionItem = styled(TicketMenuItem)`
  font-size: ${fontSize('base')};
  font-weight: ${fontWeight('semibold')};
  color: ${({ theme }) => theme.palette.accent.link};
  background-color: ${({ theme }) => theme.palette.surface.base};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const OptionsList = styled(FlexColumn)`
  gap: 0;
`;

export const supportsTicketingCreate = (integration: TicketingIntegration) =>
  integration.type === 'jira' || integration.type === 'linear';

export type ConnectTicketSearchPanelProps = {
  envId: string;
  enabledIntegrations: TicketingIntegration[];
  /** When false, ticket search is paused (e.g. popover closed). */
  isActive: boolean;
  isAttaching?: boolean;
  isCreatingTicket?: boolean;
  onSelect: (
    integration: TicketingIntegration,
    ticket: TicketSelection,
  ) => void | Promise<void>;
  onCreateClick?: (integration: TicketingIntegration) => void;
  /** Called after successful attach or after create is triggered. */
  onClose?: () => void;
};

const ConnectTicketSearchPanel = ({
  envId,
  enabledIntegrations,
  isActive,
  isAttaching = false,
  isCreatingTicket = false,
  onSelect,
  onCreateClick,
  onClose,
}: ConnectTicketSearchPanelProps) => {
  const [activeIntegrationId, setActiveIntegrationId] = useState<string>(
    enabledIntegrations[0]?.id ?? '',
  );
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    if (
      enabledIntegrations.length > 0 &&
      (!activeIntegrationId ||
        !enabledIntegrations.some((i) => i.id === activeIntegrationId))
    ) {
      setActiveIntegrationId(enabledIntegrations[0]?.id ?? '');
    }
  }, [enabledIntegrations, activeIntegrationId]);

  useEffect(() => {
    if (isActive) {
      setSearchInput('');
    }
  }, [isActive]);

  const activeIntegration = useMemo(
    () => enabledIntegrations.find((i) => i.id === activeIntegrationId),
    [activeIntegrationId, enabledIntegrations],
  );

  const {
    results: searchResults = [],
    isSearching,
    isError: isSearchError,
  } = useTicketSearch({
    envId,
    integrationId: activeIntegration?.id ?? '',
    query: searchInput,
    enabled: isActive && !!activeIntegration,
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(extractTicketKey(e.target.value));
  };

  const handleResultClick = async (ticket: TicketSelection) => {
    if (isAttaching || !activeIntegration) return;
    try {
      await onSelect(activeIntegration, ticket);
      onClose?.();
    } catch {
      // keep panel open; error surfaced by mutation snackbar
    }
  };

  const handleCreateActionClick = () => {
    if (isCreatingTicket || !activeIntegration || !onCreateClick) return;
    onCreateClick(activeIntegration);
    onClose?.();
  };

  const showProviderDropdown = enabledIntegrations.length > 1;
  const showCreateTicketAction =
    !!activeIntegration &&
    !!onCreateClick &&
    supportsTicketingCreate(activeIntegration);
  const popoverIcon = activeIntegration
    ? ticketingIconMap[activeIntegration.type]
    : null;

  const providerOptions = useMemo(
    () =>
      enabledIntegrations.map((integration) => ({
        value: integration.id,
        label: integration.name,
        icon: ticketingIconMap[integration.type],
      })),
    [enabledIntegrations],
  );

  return (
    <PanelRoot>
      <PanelHeader>
        <SearchField
          value={searchInput}
          onChange={handleSearchInputChange}
          placeholder="Search tickets…"
          fullWidth
          disabled={isAttaching || !activeIntegration}
        />
        {showProviderDropdown ? (
          <DropDown
            size="medium"
            fullWidth
            showClear={false}
            value={activeIntegrationId}
            options={providerOptions}
            onChange={(value: string) => setActiveIntegrationId(value)}
          />
        ) : null}
      </PanelHeader>
      <PanelScrollArea>
        <OptionsList>
          {showCreateTicketAction ? (
            <CreateTicketActionItem
              disabled={isCreatingTicket}
              onClick={handleCreateActionClick}
            >
              + Create ticket
              {isCreatingTicket ? (
                <CircularProgress
                  size={12}
                  sx={{ ml: 1, verticalAlign: 'middle' }}
                />
              ) : null}
            </CreateTicketActionItem>
          ) : null}
          {!activeIntegration ? null : isSearching ? (
            <Flex justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Flex>
          ) : isSearchError ? (
            <Typography
              sx={{
                px: 2,
                py: 1,
                color: 'status.error.main',
                fontSize: fontSize('base'),
              }}
            >
              Failed to load tickets
            </Typography>
          ) : searchResults.length === 0 ? (
            <Typography
              sx={{
                px: 2,
                py: 1,
                color: 'content.translucent.secondary',
                fontSize: fontSize('base'),
              }}
            >
              No tickets found
            </Typography>
          ) : (
            searchResults.map((result) => (
              <TicketMenuItem
                key={result.key}
                disabled={isAttaching}
                onClick={() => {
                  void handleResultClick(result);
                }}
              >
                <Flex
                  alignItems="center"
                  gap={1}
                  sx={{ minWidth: 0, width: '100%' }}
                >
                  {popoverIcon ? (
                    <Flex sx={{ flexShrink: 0 }}>
                      <SvgIcon width={14}>{popoverIcon}</SvgIcon>
                    </Flex>
                  ) : null}
                  <Typography
                    sx={{
                      fontWeight: fontWeight('semibold'),
                      fontSize: fontSize('base'),
                      flexShrink: 0,
                    }}
                    noWrap
                  >
                    {result.key}
                  </Typography>
                  <OverflowTooltip
                    typographySx={{
                      color: 'content.translucent.secondary',
                      minWidth: 0,
                    }}
                  >
                    {result.summary}
                  </OverflowTooltip>
                </Flex>
              </TicketMenuItem>
            ))
          )}
        </OptionsList>
      </PanelScrollArea>
    </PanelRoot>
  );
};

export default ConnectTicketSearchPanel;
